import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Alert,
  ToggleButtonGroup, ToggleButton, Button, TextField,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { getMonteCarloSimulation, MonteCarloResponse } from '../services/api';
import { runJob, watchJob, hasJob, jobKey } from '../services/backgroundJobs';
import { bloomberg } from '../theme/bloombergTheme';

interface MonteCarloProps {
  symbols?: string[];
  userWeights?: { [symbol: string]: number };
  /** Pop-up label and link when the simulation finishes while the user is elsewhere. */
  jobLabel?: string;
  jobRoute?: string;
}

const MonteCarloSimulation: React.FC<MonteCarloProps> = ({ symbols, userWeights, jobLabel, jobRoute }) => {
  const [data, setData] = useState<MonteCarloResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [horizon, setHorizon] = useState<number>(5);
  const [initialValue, setInitialValue] = useState<number>(100000);

  // Runs as a background job (services/backgroundJobs): it keeps going if the
  // user leaves, a pop-up says when it's ready, and coming back shows it.
  const key = useMemo(() => jobKey('mc', { s: symbols || [], w: userWeights || {}, h: horizon, v: initialValue }),
    [symbols, userWeights, horizon, initialValue]);
  const latestKey = useRef(key);
  latestKey.current = key;
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => (loading || data ? watchJob(key) : undefined), [key, loading, data]);

  const runSimulation = useCallback(async () => {
    if (!symbols || symbols.length < 1 || !userWeights) {
      setError('Portfolio symbols and weights are required');
      return;
    }
    const k = key;
    setLoading(true);
    setError(null);
    try {
      const result = await runJob(k, {
        label: jobLabel || 'Monte Carlo simulation',
        route: jobRoute || `${window.location.pathname}${window.location.search}`,
      }, () => getMonteCarloSimulation(symbols, userWeights, horizon, initialValue, '3y'));
      if (!mounted.current || latestKey.current !== k) return;
      if (result.error) {
        setError(result.error);
        setData(null);
      } else {
        setData(result);
      }
    } catch (err: any) {
      if (!mounted.current) return;
      console.error('Monte Carlo error:', err);
      setError(err?.response?.data?.detail || err.message || 'Simulation failed');
      setData(null);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [symbols, userWeights, horizon, initialValue, key, jobLabel, jobRoute]);

  // Back from elsewhere (e.g. the "ready" pop-up): pick up a running or finished run.
  useEffect(() => {
    if (hasJob(key)) runSimulation();
  }, [key]);

  const chartData = useMemo(() => {
    const b = data?.percentile_bands;
    if (!b || !b.p50) return [];
    return b.p50.map((_, i) => ({
      month: i + 1,
      outer: [b.p5[i], b.p95[i]],
      inner: [b.p25[i], b.p75[i]],
      median: b.p50[i],
    }));
  }, [data]);

  const fmtCurrency = (val: number) =>
    val.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

  if (!symbols || symbols.length < 1) {
    return (
      <Alert severity="info">
        Add holdings to your portfolio to run a Monte Carlo simulation.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>Horizon:</Typography>
        <ToggleButtonGroup
          value={horizon}
          exclusive
          onChange={(_, v) => v !== null && setHorizon(v)}
          size="small"
        >
          <ToggleButton value={1}>1Y</ToggleButton>
          <ToggleButton value={3}>3Y</ToggleButton>
          <ToggleButton value={5}>5Y</ToggleButton>
          <ToggleButton value={10}>10Y</ToggleButton>
        </ToggleButtonGroup>

        <TextField
          label="Initial Investment"
          type="number"
          size="small"
          value={initialValue}
          onChange={(e) => setInitialValue(Number(e.target.value) || 100000)}
          sx={{ width: 160 }}
          InputProps={{ inputProps: { min: 1000, step: 10000 } }}
        />

        <Button
          variant="contained"
          startIcon={<PlayArrowIcon />}
          onClick={runSimulation}
          disabled={loading}
          size="small"
        >
          Run Simulation
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2, alignSelf: 'center', maxWidth: 420 }}>
            Running 10,000 simulations. This can take a few minutes. You can keep using the app;
            a pop-up will tell you when it's ready.
          </Typography>
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
      ) : data ? (
        <>
          {/* Chart: the range of outcomes over time, drawn from the same run as the figures below */}
          {chartData.length > 0 && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Range of outcomes</Typography>
              <Typography variant="caption" color="text.secondary">
                Middle line: median path. Dark band: middle 50% of outcomes. Light band: 90% of outcomes.
              </Typography>
              <Box sx={{ height: 360, mt: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={{ top: 10, right: 16, bottom: 4, left: 8 }}>
                    <CartesianGrid stroke={bloomberg.border.primary} strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: bloomberg.text.muted }}
                      tickFormatter={(m: number) => (m % 12 === 0 ? `Y${m / 12}` : '')} interval={0} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: bloomberg.text.muted }} width={70}
                      tickFormatter={(v: number) => (v >= 1e6 ? `$${(v / 1e6).toFixed(1)}m` : `$${Math.round(v / 1000)}k`)} />
                    <Tooltip
                      contentStyle={{ background: bloomberg.bg.tertiary, border: `1px solid ${bloomberg.border.secondary}`, fontSize: 12 }}
                      labelFormatter={(m: number) => `Month ${m}`}
                      formatter={(v: any, name: string) => [Array.isArray(v) ? `${fmtCurrency(v[0])} – ${fmtCurrency(v[1])}` : fmtCurrency(v),
                        name === 'outer' ? '90% of outcomes' : name === 'inner' ? 'Middle 50%' : 'Median']} />
                    <Area dataKey="outer" stroke="none" fill="#2196f3" fillOpacity={0.15} isAnimationActive={false} />
                    <Area dataKey="inner" stroke="none" fill="#2196f3" fillOpacity={0.3} isAnimationActive={false} />
                    <Line dataKey="median" stroke="#2196f3" strokeWidth={2} dot={false} isAnimationActive={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          )}

          {/* Summary Cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 2 }}>
            <Paper sx={{ p: 2, borderLeft: `4px solid ${bloomberg.status.positive}` }}>
              <Typography variant="caption" color="text.secondary">Expected Value</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {fmtCurrency(data.summary.median_terminal_value)}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                Mean: {fmtCurrency(data.summary.expected_terminal_value)}
              </Typography>
            </Paper>

            <Paper sx={{ p: 2, borderLeft: `4px solid ${data.summary.prob_loss > 30 ? bloomberg.status.negative : bloomberg.status.positive}` }}>
              <Typography variant="caption" color="text.secondary">Probability of Loss</Typography>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: data.summary.prob_loss > 30 ? bloomberg.status.negative : bloomberg.status.positive }}
              >
                {data.summary.prob_loss.toFixed(1)}%
              </Typography>
              <Typography variant="caption" color="text.disabled">
                Worst 5%: {fmtCurrency(data.summary.worst_case_5pct)}
              </Typography>
            </Paper>

            <Paper sx={{ p: 2, borderLeft: '4px solid #9c27b0' }}>
              <Typography variant="caption" color="text.secondary">Probability of 2x</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#9c27b0' }}>
                {data.summary.prob_doubling.toFixed(1)}%
              </Typography>
              <Typography variant="caption" color="text.disabled">
                Best 5%: {fmtCurrency(data.summary.best_case_5pct)}
              </Typography>
            </Paper>

            <Paper sx={{ p: 2, borderLeft: '4px solid #2196f3' }}>
              <Typography variant="caption" color="text.secondary">90% Confidence Interval</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#2196f3', fontSize: '0.95rem' }}>
                {fmtCurrency(data.summary.ci_90_lower)} – {fmtCurrency(data.summary.ci_90_upper)}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                Return: {data.input_params.annualized_return.toFixed(1)}% | Vol: {data.input_params.annualized_volatility.toFixed(1)}%
              </Typography>
            </Paper>
          </Box>
        </>
      ) : (
        <Alert severity="info" sx={{ mt: 2 }}>
          Click "Run Simulation" to generate 10,000 Monte Carlo paths for your portfolio.
        </Alert>
      )}
    </Box>
  );
};

export default MonteCarloSimulation;
